import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicAPI } from '../lib/api';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { ExternalLink, Quote } from 'lucide-react';
import { useT } from '../lib/i18n';
import { useAuth } from '../lib/auth';

const PB_FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Homepage design-language styles for the Reading List. Every colour is a CMS
// brand token (theme_colors.website) used through var(--color-*) with a
// Carlos-brand fallback — change the theme and the page re-themes, nothing
// hardcoded. Matches the sibling internal pages shipped in s118 (Book/Company
// detail, Companies listing): Plus Jakarta display, gold accent, sand borders,
// pill buttons. Kept in one scoped <style> so hover states can use var()
// without Tailwind arbitrary-value comma pitfalls.
const READING_CSS = `
.rl-card{cursor:pointer;text-align:center}
.rl-cover{position:relative;overflow:hidden;border-radius:12px;margin:0 auto;width:100%;max-width:190px;
  aspect-ratio:2/3;border:1px solid var(--color-card-border,#E5E7EB);
  box-shadow:0 12px 30px -18px rgba(17,21,28,.4);transition:box-shadow .3s,border-color .2s,transform .2s}
.rl-card:hover .rl-cover{box-shadow:0 22px 46px -20px rgba(17,21,28,.55);border-color:var(--color-accent,#C9A24B);transform:translateY(-3px)}
.rl-cover img{width:100%;height:100%;object-fit:cover;transition:transform .4s ease-out}
.rl-card:hover .rl-cover img{transform:scale(1.05)}
.rl-pill{display:inline-flex;align-items:center;justify-content:center;gap:8px;border-radius:999px;
  font-weight:600;font-size:13px;cursor:pointer;transition:all .2s;font-family:inherit;text-decoration:none}
.rl-pill-solid{background:var(--color-button-bg,#11151C);color:var(--color-button-text,#fff);
  border:2px solid var(--color-button-bg,#11151C);padding:11px 22px}
.rl-pill-solid:hover{opacity:.9}
.rl-pill-outline{background:transparent;color:var(--color-heading,#11151C);
  border:2px solid var(--color-heading,#11151C);padding:11px 22px}
.rl-pill-outline:hover{background:var(--color-button-bg,#11151C);color:var(--color-button-text,#fff);border-color:var(--color-button-bg,#11151C)}
`;

export default function ReadingListPage() {
  const tt = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!(user && (user.id || user.member_id || user.username || user.email));
  const [books, setBooks] = useState([]);
  const [selected, setSelected] = useState(null);

  // Auth-gated click: anonymous visitors keep the public modal; logged-in members
  // go to the internal book detail page (info + related KMS posts).
  const openBook = (book) => {
    if (isLoggedIn) navigate(`/reading-list/${book.id}`);
    else setSelected(book);
  };

  useEffect(() => { publicAPI.getBooks().then(r => setBooks(r.data)).catch(console.error); }, []);

  // Hidden books are dropped server-side; login_required books show only to members.
  const visibleBooks = books.filter(b => !b.login_required || isLoggedIn);

  return (
    <div data-testid="reading-list-page" style={{ fontFamily: PB_FONT, backgroundColor: 'var(--color-bg, #fff)' }}>
      <style>{READING_CSS}</style>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 pt-24 md:pt-28">
        {/* Inspirational quote */}
        <div className="p-8 md:p-14 rounded-2xl mb-14 text-center" style={{ backgroundColor: '#FBFAF7', border: '1px solid var(--color-border, #e8dccd)' }}>
          <Quote className="w-9 h-9 mx-auto mb-5" style={{ color: 'var(--color-accent, #C9A24B)' }} fill="currentColor" />
          <blockquote className="text-2xl md:text-[28px] max-w-3xl mx-auto leading-[1.4] font-medium tracking-[-0.01em]" style={{ fontFamily: PB_FONT, color: 'var(--color-heading, #11151C)' }}>
            “A reader lives a thousand lives before he dies. The man who never reads lives only one.”
          </blockquote>
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold mt-6" style={{ color: 'var(--color-accent, #C9A24B)' }}>George R.R. Martin</p>
        </div>

        {/* Introduction */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: 'var(--color-accent, #C9A24B)' }}>Curated Recommendations</p>
          <h2 className="text-3xl md:text-[40px] font-extrabold tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: PB_FONT, color: 'var(--color-heading, #11151C)' }}>Books That Shape Our Thinking</h2>
          <p className="mt-4 text-[15px] leading-relaxed" style={{ color: '#4B5563' }}>These carefully selected books have profoundly influenced our consulting philosophy. Each one offers unique insights into leadership, strategy, and business transformation.</p>
        </div>

        {/* Book grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
          {visibleBooks.map(book => (
            <div key={book.id} className="rl-card group" onClick={() => openBook(book)} data-testid={`book-card-${book.id}`}>
              <div className="rl-cover">
                <img src={book.image} alt={tt(book.title)} loading="lazy" />
              </div>
              <h3 className="text-[15px] font-semibold mt-4 leading-snug line-clamp-2" style={{ fontFamily: PB_FONT, color: '#111827' }}>{tt(book.title)}</h3>
              <p className="text-[13px] mt-1" style={{ color: 'var(--color-accent, #C9A24B)' }}>by {tt(book.author)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Book detail modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto p-0" data-testid="book-detail-modal" style={{ fontFamily: PB_FONT }}>
          {selected && (
            <div>
              <style>{READING_CSS}</style>
              <div className="flex flex-col md:flex-row gap-6 p-6 pb-0">
                <img src={selected.image} alt={tt(selected.title)} className="w-36 h-52 object-cover rounded-xl shadow-lg mx-auto md:mx-0 flex-shrink-0" style={{ border: '1px solid var(--color-card-border, #E5E7EB)' }} />
                <div>
                  <h3 className="text-2xl font-extrabold tracking-[-0.02em] leading-tight" style={{ fontFamily: PB_FONT, color: 'var(--color-heading, #11151C)' }}>{tt(selected.title)}</h3>
                  <p className="text-sm font-semibold mt-1.5" style={{ color: 'var(--color-accent, #C9A24B)' }}>by {tt(selected.author)}</p>
                  <p className="text-sm mt-3 leading-relaxed" style={{ color: '#4B5563' }}>{tt(selected.description)}</p>
                </div>
              </div>
              <div className="p-6 space-y-5">
                {tt(selected.synopsis) && (
                  <div>
                    <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--color-accent, #C9A24B)' }}>Synopsis</h4>
                    <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>{tt(selected.synopsis)}</p>
                  </div>
                )}
                {tt(selected.who_is_it_for) && (
                  <div>
                    <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--color-accent, #C9A24B)' }}>Who Is It For?</h4>
                    <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>{tt(selected.who_is_it_for)}</p>
                  </div>
                )}
                {tt(selected.about_author) && (
                  <div>
                    <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--color-accent, #C9A24B)' }}>About the Author</h4>
                    <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>{tt(selected.about_author)}</p>
                  </div>
                )}
                <div className="flex gap-3 pt-2 flex-wrap">
                  {selected.amazon_link && (
                    <a href={selected.amazon_link} target="_blank" rel="noreferrer" className="rl-pill rl-pill-solid" data-testid="book-amazon-link">
                      <ExternalLink className="w-4 h-4" /> Buy on Amazon
                    </a>
                  )}
                  {selected.other_links?.map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noreferrer" className="rl-pill rl-pill-outline">
                      <ExternalLink className="w-4 h-4" /> {tt(link.name)}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
